import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import OutgoingEdit from '@app/invoices/outgoing-invoices/outgoing-edit';
import Home from '@/app';
import AccessDenied from '@/app/access-denied';
import Account from '@/app/account';
import AccountEdit from '@/app/account/account-edit';
import Activity from '@/app/activity';
import Calculator from '@/app/calculator';
import Categories from '@/app/categories';
import Companies from '@/app/companies';
import CompanyCreate from '@/app/companies/company-create';
import CompanyEdit from '@/app/companies/company-edit';
import CompanyView from '@/app/companies/company-view';
import ContactUs from '@/app/contact-us';
import Employees from '@/app/employees';
import EmployeeCreate from '@/app/employees/employee-create';
import EmployeeEdit from '@/app/employees/employee-edit';
import EmployeeUpload from '@/app/employees/employee-upload';
import EmployeeView from '@/app/employees/employee-view';
import EmployeesLeavesOverview from '@/app/employees/leaves-overview';
import Excel from '@/app/exports';
import FinancialStatements from '@/app/financial-statements';
import GroupReporting from '@/app/group-reporting';
import ClientReports from '@/app/client-reports';
import MyReports from '@/app/my-reports';
import TaxReturns from '@/app/tax-returns';
import Guidelines from '@/app/guidelines';
import HrRequests from '@/app/hr-requests';
import ArchiveInvoices from '@/app/invoices/archive-invoices';
import ArchiveEdit from '@/app/invoices/archive-invoices/archive-edit';
import ArchiveView from '@/app/invoices/archive-invoices/archive-view';
import NewInvoice from '@/app/invoices/create-invoices';
import CustomersInvoices from '@/app/invoices/customers-invoices';
import CustomerCreate from '@/app/invoices/customers-invoices/customer-create';
import CustomerEdit from '@/app/invoices/customers-invoices/customer-edit';
import CustomerView from '@/app/invoices/customers-invoices/customer-view';
import IncomingInvoices from '@/app/invoices/incoming-invoices';
import IncomingView from '@/app/invoices/incoming-invoices/incoming-view';
import OutgoingInvoices from '@/app/invoices/outgoing-invoices';
import OutgoingView from '@/app/invoices/outgoing-invoices/outgoing-view';
import ProductsInvoices from '@/app/invoices/products-invoices';
import ProductCreate from '@/app/invoices/products-invoices/product-create';
import ProductEdit from '@/app/invoices/products-invoices/product-edit';
import ProductView from '@/app/invoices/products-invoices/product-view';
import MyTicketView from '@/app/my-tickets/my-ticket-view';
import NotFound from '@/app/not-found';
import EnergyTools from '@/app/energy-tools';
import Fleet from '@/app/fleet';
import OzEngine from '@/app/oz';
import PayrollAutomation from '@/app/payroll-automation';
import Sectors from '@/app/sectors';
import SectorCreate from '@/app/sectors/sector-create';
import SectorEdit from '@/app/sectors/sector-edit';
import SectorView from '@/app/sectors/sector-view';
import SmartParser from '@/app/smart-parser';
import KnowledgeBase from '@/app/knowledge-base';
import SupportTickets from '@/app/support-tickets';
import SupportTicketView from '@/app/support-tickets/support-ticket-view';
import TechAdmin from '@/app/tech-admin';
import AssistantWidget from '@/components/assistant/AssistantWidget';
import TimeTracking from '@/app/time-tracking';
import TimeTrackingApproval from '@/app/time-tracking/approval';
import TimeTrackingProjects from '@/app/time-tracking/projects';
import TimeTrackingProjectDetail from '@/app/time-tracking/projects/project-detail';
import TimeTrackingReports from '@/app/time-tracking/reports';
import TimeSheet from '@/app/timesheet';
import TimeSheetCalendar from '@/app/timesheet/timesheet-calendar';
import TimeSheetExport from '@/app/timesheet/timesheet-export';
import TimeSheetUpload from '@/app/timesheet/timesheet-upload';
import Tools from '@/app/tools';
import Users from '@/app/users';
import UserCreate from '@/app/users/user-create';
import UserEdit from '@/app/users/user-edit';
import UserView from '@/app/users/user-view';
import { Loading } from '@/components/loading';
import { ROUTES } from '@/constants';

const AppDataProvider = lazy(() =>
  import('@/contexts/app-data/AppDataProvider').then((module) => ({
    default: module.AppDataProvider,
  }))
);

export const privateRoutes = () => {
  const routes = [
    {
      element: (
        <AppDataProvider>
          {/*
            🚨 Without a boundary here, the only Suspense in the app is the one
            in main.jsx wrapping the whole tree — so anything that suspends
            inside a page (a lazily imported panel, an i18n namespace that has
            to be fetched) unmounts EVERYTHING down to the providers and paints
            the full-screen loader over the app until it resolves.

            ⚠️ This does NOT keep the sidebar on screen while a lazy route
            chunk downloads: all 50 pages render their own RootLayout, so the
            chrome lives inside the chunk and goes away with it. Hoisting the
            layout above this Outlet is what would fix that, and it is a
            separate change.
          */}
          <Suspense fallback={<Loading show={true} />}>
            <Outlet />
          </Suspense>
          <AssistantWidget />
        </AppDataProvider>
      ),
      children: [
        {
          index: true,
          element: <Home />,
        },
        {
          path: ROUTES.users,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Users />,
            },
            {
              path: ROUTES.userCreate,
              element: <UserCreate />,
            },
            {
              path: ROUTES.userView,
              element: <UserView />,
            },
            {
              path: ROUTES.userEdit,
              element: <UserEdit />,
            },
          ],
        },
        {
          path: ROUTES.companies,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Companies />,
            },
            {
              path: ROUTES.companyCreate,
              element: <CompanyCreate />,
            },
            {
              path: ROUTES.companyView,
              element: <CompanyView />,
            },
            {
              path: ROUTES.companyEdit,
              element: <CompanyEdit />,
            },
          ],
        },
        {
          path: ROUTES.sectors,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Sectors />,
            },
            {
              path: ROUTES.sectorCreate,
              element: <SectorCreate />,
            },
            {
              path: ROUTES.sectorView,
              element: <SectorView />,
            },
            {
              path: ROUTES.sectorEdit,
              element: <SectorEdit />,
            },
          ],
        },
        {
          path: ROUTES.employees,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Employees />,
            },
            {
              path: ROUTES.employeeCreate,
              element: <EmployeeCreate />,
            },
            {
              path: ROUTES.employeeView,
              element: <EmployeeView />,
            },
            {
              path: ROUTES.employeeEdit,
              element: <EmployeeEdit />,
            },
            {
              path: ROUTES.employeeUpload,
              element: <EmployeeUpload />,
            },
            {
              path: ROUTES.employeesLeavesOverview,
              element: <EmployeesLeavesOverview />,
            },
          ],
        },
        {
          path: ROUTES.timeTracking,
          element: <TimeTracking />,
          children: [
            {
              index: true,
              element: <TimeTrackingProjects />,
            },
            {
              path: ROUTES.timeTrackingProjectView,
              element: <TimeTrackingProjectDetail />,
            },
            {
              path: ROUTES.timeTrackingApproval,
              element: <TimeTrackingApproval />,
            },
            {
              path: ROUTES.timeTrackingReports,
              element: <TimeTrackingReports />,
            },
          ],
        },
        {
          path: ROUTES.time_sheet,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <TimeSheet />,
            },
            {
              path: ROUTES.time_sheetCalendar,
              element: <TimeSheetCalendar />,
            },
            {
              path: ROUTES.time_sheetExport,
              element: <TimeSheetExport />,
            },
            {
              path: ROUTES.time_sheetUpload,
              element: <TimeSheetUpload />,
            },
          ],
        },
        {
          path: ROUTES.exportToExcel,
          element: <Excel />,
        },
        {
          path: ROUTES.hrRequest,
          element: <HrRequests />,
        },
        {
          path: ROUTES.calculator,
          element: <Calculator />,
        },
        {
          path: ROUTES.invoicesCreate,
          element: <NewInvoice />,
        },
        {
          path: ROUTES.outgoingInvoices,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <OutgoingInvoices />,
            },
            {
              path: ROUTES.outgoingInvoiceView,
              element: <OutgoingView />,
            },
            {
              path: ROUTES.outgoingInvoiceEdit,
              element: <OutgoingEdit />,
            },
          ],
        },
        {
          path: ROUTES.incomingInvoices,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <IncomingInvoices />,
            },
            {
              path: ROUTES.incomingInvoiceView,
              element: <IncomingView />,
            },
          ],
        },
        {
          path: ROUTES.customersInvoices,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <CustomersInvoices />,
            },
            {
              path: ROUTES.customerInvoicesCreate,
              element: <CustomerCreate />,
            },
            {
              path: ROUTES.customerInvoiceView,
              element: <CustomerView />,
            },
            {
              path: ROUTES.customerInvoicesEdit,
              element: <CustomerEdit />,
            },
          ],
        },
        {
          path: ROUTES.productsInvoices,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <ProductsInvoices />,
            },
            {
              path: ROUTES.productCreate,
              element: <ProductCreate />,
            },
            {
              path: ROUTES.productView,
              element: <ProductView />,
            },
            {
              path: ROUTES.productEdit,
              element: <ProductEdit />,
            },
          ],
        },
        {
          path: ROUTES.archiveInvoices,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <ArchiveInvoices />,
            },
            {
              path: ROUTES.archiveInvoiceView,
              element: <ArchiveView />,
            },
            {
              path: ROUTES.archiveInvoiceEdit,
              element: <ArchiveEdit />,
            },
          ],
        },
        {
          path: ROUTES.guidelines,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Guidelines />,
            },
          ],
        },
        {
          path: ROUTES.activity,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Activity />,
            },
          ],
        },
        {
          path: ROUTES.tools,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Tools />,
            },
          ],
        },
        {
          path: ROUTES.categories,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Categories />,
            },
          ],
        },
        {
          path: ROUTES.techAdmin,
          element: <TechAdmin />,
        },
        {
          path: ROUTES.knowledgeBase,
          element: <KnowledgeBase />,
        },
        {
          path: ROUTES.oz,
          element: <OzEngine />,
        },
        {
          path: ROUTES.energyTools,
          element: <EnergyTools />,
        },
        {
          path: ROUTES.fleet,
          element: <Fleet />,
        },
        {
          path: ROUTES.payrollAutomation,
          element: <PayrollAutomation />,
        },
        {
          path: ROUTES.smartParser,
          element: <SmartParser />,
        },
        {
          path: ROUTES.financialStatements,
          element: <FinancialStatements />,
        },
        {
          path: ROUTES.groupReporting,
          element: <GroupReporting />,
        },
        {
          path: ROUTES.clientReports,
          element: <ClientReports />,
        },
        {
          path: ROUTES.myReports,
          element: <MyReports />,
        },
        {
          path: ROUTES.taxReturns,
          element: <TaxReturns />,
        },
        {
          path: ROUTES.account,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <Account />,
            },
            {
              path: ROUTES.accountEdit,
              element: <AccountEdit />,
            },
          ],
        },
        {
          path: ROUTES.contactUs,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <ContactUs />,
            },
            {
              path: ROUTES.supportTicketView,
              element: <MyTicketView />,
            },
          ],
        },
        {
          path: ROUTES.supportTickets,
          element: <Outlet />,
          children: [
            {
              index: true,
              element: <SupportTickets />,
            },
            {
              path: ROUTES.supportTicketView,
              element: <SupportTicketView />,
            },
          ],
        },
        {
          path: ROUTES.accessDenied,
          element: <AccessDenied />,
        },
        {
          path: ROUTES.notFound,
          element: <NotFound />,
        },
      ],
    },
  ];
  return routes;
};

export default privateRoutes;
